import { Core } from "@faire/design-tokens";
import { QueryParameters } from "@faire/web-api/api/product/subscribed/retailerToken/back-in-stock/get";
import { useQuery as useQuerySubscribedBackInStock } from "@faire/web-api/api/product/subscribed/retailerToken/back-in-stock/get-hooks";
import {
  trackCartAddToCartWebCujClick,
  trackCartAddToCartWebCujClickTraceUalComplete,
} from "@faire/web-api/events/cart/click/addToCartWebCuj";
import { IInformationMessage } from "@faire/web-api/indigofair/data/IInformationMessage";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { moneyOrZero } from "@faire/web/common/money";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { LazySamplesRequestModal } from "@faire/retailer/components/Modals/SamplesRequestModal/LazySamplesRequestModal";
import { ISamplesBannerDetails } from "@faire/retailer/components/Modals/SamplesRequestModal/RequestSamplesCTA";
import { SamplesRequestStoreProvider } from "@faire/retailer/components/Modals/SamplesRequestModal/SamplesRequestStore";
import { TrackedLinkContext } from "@faire/retailer/components/TrackedLink";
import { AddToCartNotificationFinished } from "@faire/retailer/events";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { getIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import {
  isLoggedInRetailerOrBrandPreview,
  useIsLoggedInRetailer,
} from "@faire/retailer/lib/isLoggedInRetailer";
import { useSettingFaireDirectPDPSignup } from "@faire/retailer/settings/useSettingFaireDirectPDPSignup";
import { AddToCartVariants } from "@faire/retailer/stores/domain/CartStore/triggerAddToCartEvent";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { useFaireProtection } from "@faire/retailer/stores/domain/FaireProtection/useFaireProtection";
import { getRetailerToken } from "@faire/retailer/stores/domain/RetailerStore";
import { DesktopFaireDirectPerksCard } from "@faire/retailer/views/product/Components/DesktopFaireDirectPerksCard";
import { MobileFaireDirectPerksCard } from "@faire/retailer/views/product/Components/MobileFaireDirectPerksCard";
import { ProductPerksCard } from "@faire/retailer/views/product/Components/ProductPerksCard";
import { Customization } from "@faire/retailer/views/product/ProductDetails/AddToCart/Customization";
import { OptionSelection } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/OptionSelection";
import { PreorderMessage } from "@faire/retailer/views/product/ProductDetails/AddToCart/PreorderMessage";
import { ProductAddToCartState } from "@faire/retailer/views/product/ProductDetails/AddToCart/ProductAddToCartState";
import {
  trackAddToCartEvent,
  trackEditQuantityEvent,
  trackSKUViewEvent,
  trackTaxonomyProductLanguageOptions,
  trackViewRequestSamplesCTAEvent,
} from "@faire/retailer/views/product/ProductDetails/AddToCart/TrackEvents";
import { ShippingDetailsList } from "@faire/retailer/views/product/ProductDetails/ShippingDetailsList";
import { VariantLevelInfo } from "@faire/retailer/views/product/ProductDetails/VariantLevelInfo";
import { getProductOptions } from "@faire/retailer/views/product/utils/productUtils";

import { AddToCartButton } from "./__internal__/ProductAddToCart/AddToCartButton";
import { QuantitySelector } from "./__internal__/ProductAddToCart/QuantitySelector";
import { TesterCheckbox } from "./__internal__/ProductAddToCart/TesterCheckbox";

interface IProps {
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  productAddToCartState: ProductAddToCartState;
  beforeAddToCart?: () => void;
  afterAddToCart?: () => void;
  productAddCTAOverride?: (
    isUpdating: boolean,
    strictLocalize: StrictLocalizeFunction
  ) => string;
  containingModalElement?: HTMLElement;
  pathName?: string;
  onAddToCart?: (productOptionToken: string | undefined) => void;
  onChangeQuantity?: (
    productOptionToken: string | undefined,
    productQuantityInCart: number,
    selectedQuantity: number
  ) => void;
  preselectedProductOptionToken?: string;
  allowCustomOption?: boolean;
  isInvoice?: boolean;
  sampleDetails?: ISamplesBannerDetails;
  infoTexts?: IInformationMessage[];
  importShipmentInfo?: string;
  isCartInfoLoading?: boolean;
  variant: AddToCartVariants;
  showSideCart?: boolean;
}

const ProductAddToCartBase: React.FC<IProps> = ({
  brand,
  product,
  productAddToCartState,
  productAddCTAOverride,
  containingModalElement,
  isInvoice,
  preselectedProductOptionToken,
  sampleDetails,
  onChangeQuantity,
  onAddToCart,
  beforeAddToCart,
  afterAddToCart,
  variant,
  isCartInfoLoading,
  allowCustomOption,
  importShipmentInfo,
  infoTexts,
  showSideCart,
}) => {
  const typedContext = React.useContext(TrackedLinkContext);

  const { isBrandTokenForbidden, isLoading: isFaireProtectionLoading } =
    useFaireProtection();
  const { isPreOrderExcludedForBrandPromo } = useMarketStore([
    "isPreOrderExcludedForBrandPromo",
  ]);
  const { pathname, search } = useLocation();
  const isLoggedInRetailer = useIsLoggedInRetailer();

  const [addToCartNotificationInProgress, setAddToCartNotificationInProgress] =
    useState(false);
  const [showUnderMOQPerStyleError, setShowUnderMOQPerStyleError] =
    useState(false);
  const [shouldShowSampleRequestModal, setShouldShowSampleRequestModal] =
    useState(false);
  const [subscribedBackInStockTokens, setSubscribedBackInStockTokens] =
    useState<string[]>([]);

  const backOrderedProductOptionTokens = getProductOptions(product)
    .filter((option) => {
      return (
        option.state === IProduct.State.SALES_PAUSED &&
        option.token !== undefined
      );
    })
    .map((option) => {
      return option.token ?? "";
    });

  const { data: subscribedBackInStockData } = useQuerySubscribedBackInStock(
    getRetailerToken(),
    QueryParameters.build({
      product_option_tokens: backOrderedProductOptionTokens,
    }),
    {
      enabled: isLoggedInRetailer,
    }
  );

  useEffect(() => {
    if (subscribedBackInStockData?.product_option_tokens) {
      setSubscribedBackInStockTokens(
        subscribedBackInStockData.product_option_tokens
      );
    }
  }, [subscribedBackInStockData?.product_option_tokens]);

  useEffect(() => {
    AddToCartNotificationFinished.subscribe(turnOffAddToCartNotification);
    return () => {
      AddToCartNotificationFinished.unsubscribe(turnOffAddToCartNotification);
    };
  }, []);

  useEffect(() => {
    return () => {
      productAddToCartState.cleanup();
    };
  }, [productAddToCartState]);

  const viewSampleButtonRef = useDoActionWhenVisible(() => {
    // send view event only if it is part of pilot group or in treatment group
    trackViewRequestSamplesCTAEvent(brand.token, product.token);
  });

  const turnOffAddToCartNotification = () => {
    setAddToCartNotificationInProgress(false);
  };

  const updateCart = () => {
    trackCartAddToCartWebCujClick();
    beforeAddToCart?.();

    if (getIsBrandPreview()) {
      return Promise.reject();
    }

    const {
      isOpenSizingVariant,
      isUnderPerStyleMOQ,
      areAllOptionQuantitiesValid,
      selectedQuantity,
      productOptionInCart,
    } = productAddToCartState;

    if (
      isOpenSizingVariant &&
      (isUnderPerStyleMOQ || !areAllOptionQuantitiesValid)
    ) {
      if (isUnderPerStyleMOQ) {
        setShowUnderMOQPerStyleError(true);
      }
      return Promise.reject();
    }

    setShowUnderMOQPerStyleError(false);
    trackEditQuantityEvent(
      selectedQuantity,
      productOptionInCart,
      brand.token,
      pathname,
      onChangeQuantity
    );
    trackAddToCartEvent(
      search,
      selectedOption,
      productOptionInCart,
      product.token,
      pathname,
      typedContext,
      onAddToCart
    );
    trackTaxonomyProductLanguageOptions(selectedOption);

    if (isInvoice) {
      return productAddToCartState
        .addOrUpdateSelectedOptionToInvoice()
        .then(() => {
          afterAddToCart?.();
        });
    }

    return productAddToCartState
      .addOrUpdateSelectedOptionToCart(
        getWindowOrThrow().performance.now(),
        variant
      )
      .then(() => {
        setAddToCartNotificationInProgress(showSideCart ? false : true);
        // Invoke the after add to cart method if passed down from parent
        afterAddToCart?.();
        trackCartAddToCartWebCujClickTraceUalComplete();
      });
  };

  const deleteBackInStockNotification = () => {
    const { selectedOption, deleteBackInStockNotification } =
      productAddToCartState;

    deleteBackInStockNotification();

    if (subscribedBackInStockTokens) {
      const updatedTokens = subscribedBackInStockTokens.filter(
        (token) => token !== selectedOption?.token
      );
      setSubscribedBackInStockTokens(updatedTokens);
    }
  };

  const onTesterCheckboxChange = () => {
    const { setIncludesTesters, includesTesters } = productAddToCartState;
    setIncludesTesters(!includesTesters);
  };

  const isPreorderExcludedFromPromo = () => {
    const promoCollections = brand.active_promo_collections ?? [];
    return promoCollections.some((promoCollection) =>
      isPreOrderExcludedForBrandPromo(
        promoCollection.promotional_event_key,
        promoCollection.product_ship_availability
      )
    );
  };

  const {
    includesTesters,
    minimumOrderQuantity,
    isOpenSizingVariant,
    currentStyleHasOnlyPrePacks,
    selectedOption,
    perStyleOptionVariations,
  } = productAddToCartState;

  return (
    <>
      {shouldShowSampleRequestModal && brand.token !== undefined ? (
        <React.Suspense fallback={null}>
          <SamplesRequestStoreProvider>
            <LazySamplesRequestModal
              brandToken={brand.token}
              productToken={product.token}
              source="PDP"
              onClose={() => {
                setShouldShowSampleRequestModal(false);
              }}
            />
          </SamplesRequestStoreProvider>
        </React.Suspense>
      ) : null}
      <Wrapper>
        <OptionSelection
          productAddToCartState={productAddToCartState}
          product={product}
          brand={brand}
          selectedProductOptionToken={preselectedProductOptionToken}
        />
        <VariantLevelInfo
          product={product}
          selectedOption={selectedOption}
          productAddToCartState={productAddToCartState}
          actionWhenSKUVisible={() => {
            trackSKUViewEvent(
              selectedOption,
              isOpenSizingVariant,
              currentStyleHasOnlyPrePacks,
              product
            );
          }}
        />
        {product.purchase_information?.customizations?.map(
          (
            {
              token,
              required,
              additional_charge,
              instructions,
              retailer_input_max_chars,
              active,
              additional_promo_charge,
            },
            i
          ) =>
            token && active ? (
              <React.Fragment key={token || i}>
                <Customization
                  required={!!required}
                  additionalCharge={moneyOrZero(additional_charge)}
                  additionalPromoCharge={additional_promo_charge}
                  instructions={instructions || ""}
                  characterLimit={retailer_input_max_chars}
                  value={productAddToCartState.getCustomization(token)}
                  onChange={(v) =>
                    productAddToCartState.setCustomization(token, v)
                  }
                  onToggle={(v) =>
                    productAddToCartState.setCustomizationUsedInMoqCalculation(
                      token,
                      v
                    )
                  }
                />
                <Spacer size="2x" />
              </React.Fragment>
            ) : null
        )}
        {isLoggedInRetailerOrBrandPreview() || isInvoice ? (
          <>
            <TesterCheckbox
              product={product}
              includesTesters={includesTesters}
              selectedOption={selectedOption}
              perStyleOptionVariations={perStyleOptionVariations}
              onTesterCheckboxChange={onTesterCheckboxChange}
            />
            {isOpenSizingVariant ? null : (
              <QuantitySelector
                productAddToCartState={productAddToCartState}
                product={product}
                subscribedBackInStockTokens={subscribedBackInStockTokens}
                allowCustomOption={allowCustomOption}
                containingModalElement={containingModalElement}
                deleteBackInStockNotification={deleteBackInStockNotification}
              />
            )}
            <Spacer height="1x" />
            <AddToCartButton
              brandToken={brand.token}
              addToCartNotificationInProgress={addToCartNotificationInProgress}
              isBrandActive={brand.active}
              isInvoice={isInvoice}
              isCartInfoLoading={isCartInfoLoading}
              isFaireProtectionLoading={isFaireProtectionLoading}
              productAddToCartState={productAddToCartState}
              product={product}
              productAddCTAOverride={productAddCTAOverride}
              updateCart={updateCart}
              isBrandTokenForbidden={isBrandTokenForbidden}
            />
            {sampleDetails ? (
              <RequestSampleViewRefWrapper ref={viewSampleButtonRef}>
                <Spacer size="2x" />
                <RequestASampleButton
                  data-test-id="request-samples-button-pdp"
                  onClick={() => {
                    setShouldShowSampleRequestModal(true);
                  }}
                >
                  <StrictLocalMsg
                    defaultMessage="Request a sample"
                    description={{
                      text: "Request a sample button text",
                      img: "https://cdn.faire.com/fastly/856e6b4e2e78baa2138224673e6c2b44df08fe2f6c84a43b5a56d3086a2ef18f.png",
                    }}
                  />
                </RequestASampleButton>
              </RequestSampleViewRefWrapper>
            ) : null}
            {showUnderMOQPerStyleError ? (
              <>
                <Spacer size="2x" />
                <OpenSizingErrorMessage>
                  <StrictLocalMsg
                    defaultMessage="This item requires a minimum order quantity of {minimumOrderQuantityNumber}"
                    description={{
                      text: "qty as in quantity. Minimum number of items needed to order this product.",
                    }}
                    values={{
                      minimumOrderQuantityNumber: minimumOrderQuantity,
                    }}
                  />
                </OpenSizingErrorMessage>
              </>
            ) : null}
            {productAddToCartState.showPreorderMessaging ? (
              <PreorderMessage
                isOpenSizingVariant={productAddToCartState.isOpenSizingVariant}
                openSizingShipDateRange={
                  productAddToCartState.openSizingShipDateRange
                }
                openSizingAllSameDates={
                  productAddToCartState.openSizingAllSameDates
                }
                baseOrderByDate={productAddToCartState.baseOrderByDate}
                selectedOption={productAddToCartState.selectedOption}
                product={product}
                isPreorderExcludedFromPromo={isPreorderExcludedFromPromo()}
              />
            ) : null}
          </>
        ) : (
          <>
            <ShippingDetailsList
              brand={brand}
              importShipmentInfo={importShipmentInfo}
              infoTexts={infoTexts}
              shippingPromotion={
                productAddToCartState.productPromotionState
                  ?.guaranteedFreeShippingPromo
              }
            />
            <Spacer size="3x" />
            <MaybePerksCard />
          </>
        )}
      </Wrapper>
    </>
  );
};

const MaybePerksCard: React.FC = () => {
  const settingFaireDirectPDPSignup = useSettingFaireDirectPDPSignup();
  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();

  if (isFaireDirectActiveInLoggedOut) {
    return !settingFaireDirectPDPSignup ? (
      <ResponsiveContentV2
        mobile={<MobileFaireDirectPerksCard />}
        tabletAndAbove={<DesktopFaireDirectPerksCard />}
      />
    ) : null;
  }

  return <ProductPerksCard />;
};

export const ProductAddToCart = observer(ProductAddToCartBase);

const Wrapper = styled(Column).attrs({ align: "flex-start" })`
  width: 100%;
`;

const RequestASampleButton = styled(Button).attrs({
  variant: "secondary",
})`
  width: 100%;
`;

const OpenSizingErrorMessage = styled(Typography).attrs({
  color: Core.text.critical,
})`
  &&& {
    margin-top: 2px;
  }
`;

const RequestSampleViewRefWrapper = styled.div`
  width: 100%;
`;
